import { create } from 'zustand';
import { storage, onRehydrate } from '../utils/storage';

/**
 * Global Context Store
 * 
 * Manages the app-wide filter context. When a context is set,
 * pages use the `useContextFilter` hook to automatically filter
 * their data to match the active context.
 * 
 * Context types:
 * - { type: 'project', id: 'uuid' }           → Filter to a specific project
 * - { type: 'property', key: 'Client', value: 'MYSELF' } → Filter by property value
 * - null                                        → No filter (show everything)
 */

const STORAGE_KEY = 'active_context';

const useContextStore = create((set, get) => ({
    activeContext: storage.get(STORAGE_KEY, null),

    setContext(context) {
        set({ activeContext: context });
        storage.set(STORAGE_KEY, context);
    },

    clearContext() {
        set({ activeContext: null });
        storage.set(STORAGE_KEY, null);
    },

    /**
     * Quick helper — set project context by ID.
     */
    setProjectContext(projectId) {
        get().setContext({ type: 'project', id: projectId });
    },

    /**
     * Quick helper — set property context.
     */
    setPropertyContext(propertyKey, propertyValue) {
        get().setContext({ type: 'property', key: propertyKey, value: propertyValue });
    },

    /**
     * Check if a context is active.
     */
    hasContext() {
        return get().activeContext !== null;
    },
}));

onRehydrate(() => {
    useContextStore.setState({ activeContext: storage.get(STORAGE_KEY, null) });
});

export default useContextStore;
